import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';

import {
  PoPageModule,
  PoButtonModule,
  PoFieldModule,
  PoTableModule,
  PoTableColumn,
  PoPageAction,
  PoDividerModule,
  PoLoadingModule,
  PoNotificationService,
  PoSelectOption
} from '@po-ui/ng-components';

import {
  DownloadPdfService,
  SolicitacaoDownload,
  SolicitarDownloadReq,
  ZogStatus
} from './download-pdf.service';

interface SolicitacaoRow {
  cod:           string;
  filOri:        string;
  cnpj:          string;
  dataInicial:   string;
  dataFinal:     string;
  arquivosNovos: string;
  origem:        string;
  status:        ZogStatus;
  statusLabel:   string;
  criadoEm:      string;
  atualizadoEm:  string;
  usuario:       string;
  qtdArquivos:   number;
  tamanhoZip:    number;
  mensagemErro:  string;
  urlZip:        string;
}

@Component({
  selector: 'app-download-pdf',
  imports: [
    ReactiveFormsModule,
    PoPageModule,
    PoButtonModule,
    PoFieldModule,
    PoTableModule,
    PoDividerModule,
    PoLoadingModule
  ],
  templateUrl: './download-pdf.html',
  styleUrls: ['./download-pdf.css'],
})
export class DownloadPdfComponent implements OnInit {

  // Filtros do formulario (Reactive Forms - evita o bug do po-datepicker
  // que esvazia o campo enquanto o usuario digita com [ngModel]+(p-change))
  filterForm: FormGroup;

  loadingList   = false;
  enviando      = false;
  solicitacoes: SolicitacaoRow[] = [];

  readonly pageActions: Array<PoPageAction> = [
    { label: 'Gerar Download', action: () => this.gerarDownload(), icon: 'an an-download-simple' },
    { label: 'Atualizar',      action: () => this.carregar(),       icon: 'an an-arrows-clockwise' }
  ];

  readonly tipoOptions: PoSelectOption[] = [
    { label: 'NF',    value: 'NF' },
    { label: 'CTE',   value: 'CTE' },
    { label: 'Ambos', value: 'AMBOS' }
  ];

  readonly somenteNovosOptions: PoSelectOption[] = [
    { label: 'Sim', value: '1' },
    { label: 'Nao', value: '2' }
  ];

  readonly origemOptions: PoSelectOption[] = [
    { label: 'Arquivei',      value: 'ARQUIVEI' },
    { label: 'QTEC - Manual', value: 'MANUAL' },
    { label: 'Ambos',         value: 'AMBOS' }
  ];

  readonly columns: Array<PoTableColumn> = [
    { property: 'cod',           label: 'Codigo',         width: '100px' },
    { property: 'dataInicial',   label: 'Data Inicial',   width: '140px' },
    { property: 'dataFinal',     label: 'Data Final',     width: '140px' },
    { property: 'arquivosNovos', label: 'Somente Novos',  width: '140px' },
    { property: 'origem',        label: 'Origem',         width: '120px' },
    { property: 'statusLabel',   label: 'Status',         width: '160px', type: 'label', labels: [
      { value: 'PENDENTE',      color: 'color-08', label: 'PENDENTE' },
      { value: 'PROCESSANDO',   color: 'color-07', label: 'PROCESSANDO' },
      { value: 'EMAIL ENVIADO', color: 'color-10', label: 'EMAIL ENVIADO' },
      { value: 'ERRO',          color: 'color-07', label: 'ERRO' }
    ]},
    { property: 'criadoEm',      label: 'Criado Em',      width: '160px' },
    { property: 'atualizadoEm',  label: 'Atualizado Em',  width: '160px' },
    { property: 'usuario',       label: 'Usuario' }
  ];

  constructor(
    private fb: FormBuilder,
    private service: DownloadPdfService,
    private notif: PoNotificationService
  ) {
    this.filterForm = this.buildFilterForm();
  }

  ngOnInit(): void {
    this.carregar();
  }

  private buildFilterForm(): FormGroup {
    return this.fb.group({
      tipoSelecionado:   ['NF'],
      somenteNovos:      ['2'],
      origemSelecionada: ['AMBOS'],
      dataInicial:       [''],
      dataFinal:         [''],
      email:             ['']
    });
  }

  carregar(): void {
    this.loadingList = true;
    this.service.listar(1, 30).subscribe({
      next: res => {
        this.solicitacoes = res.data.map(s => this.toRow(s));
        this.loadingList = false;
      },
      error: () => {
        this.notif.error('Erro ao carregar solicitacoes.');
        this.loadingList = false;
      }
    });
  }

  gerarDownload(): void {
    const v = this.filterForm.value;
    const dataInicial = (v.dataInicial || '') as string;
    const dataFinal   = (v.dataFinal   || '') as string;

    if (!dataInicial || !dataFinal) {
      this.notif.warning('Informe Data Inicial e Data Final.');
      return;
    }
    if (dataFinal < dataInicial) {
      this.notif.warning('Data Final menor que Data Inicial.');
      return;
    }

    const req: SolicitarDownloadReq = {
      dataIni: dataInicial,
      dataFim: dataFinal,
      tipo:    v.tipoSelecionado   as SolicitarDownloadReq['tipo'],
      novos:   v.somenteNovos      as SolicitarDownloadReq['novos'],
      origem:  v.origemSelecionada as SolicitarDownloadReq['origem']
    };
    const email = (v.email || '').trim();
    if (email) {
      req.email = email;
    }

    this.enviando = true;
    this.service.solicitar(req).subscribe({
      next: s => {
        this.enviando = false;
        this.notif.success(`Solicitacao ${s.cod} registrada. Voce recebera o link por e-mail.`);
        this.solicitacoes = [this.toRow(s), ...this.solicitacoes];
      },
      error: err => {
        this.enviando = false;
        const msg = err?.error?.message ?? 'Erro ao registrar solicitacao.';
        this.notif.error(msg);
      }
    });
  }

  private toRow(s: SolicitacaoDownload): SolicitacaoRow {
    return {
      cod:           s.cod,
      filOri:        s.filOri,
      cnpj:          s.cnpj,
      dataInicial:   this.fmtDate(s.dataInicial),
      dataFinal:     this.fmtDate(s.dataFinal),
      arquivosNovos: s.novos === '1' ? 'SIM' : 'NAO',
      origem:        s.origem,
      status:        s.status,
      statusLabel:   this.statusLabel(s.status),
      criadoEm:      this.fmtDateTime(s.dataSolicitacao, s.horaSolicitacao),
      atualizadoEm:  this.fmtDateTime(s.dataProcesso || s.dataSolicitacao, s.horaProcesso || s.horaSolicitacao),
      usuario:       s.nomeUsuario || s.usuario,
      qtdArquivos:   s.qtdArquivos,
      tamanhoZip:    s.tamanhoZip,
      mensagemErro:  s.mensagemErro,
      urlZip:        s.urlZip
    };
  }

  private statusLabel(s: ZogStatus): string {
    switch (s) {
      case '1': return 'PENDENTE';
      case '2': return 'PROCESSANDO';
      case '3': return 'EMAIL ENVIADO';
      case '4': return 'ERRO';
      default:  return s;
    }
  }

  private fmtDate(iso: string): string {
    if (!iso || iso.length < 10) return '-';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
  }

  private fmtDateTime(iso: string, hora: string): string {
    const d = this.fmtDate(iso);
    if (d === '-') return '-';
    return `${d} ${hora || ''}`.trim();
  }
}
